import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {RouterLink} from '@angular/router';
import {Tag} from 'primeng/tag';
import {Timeline} from 'primeng/timeline';
import {environment} from '../../../../environments/environment';
import {AccountService} from '../../../services/account.service';
import {AlarmService} from '../../../services/alarm.service';
import {AuthService} from '../../../services/auth.service';
import {DeviceService} from '../../../services/device.service';
import {ButtonIconComponent} from '../../../shared/components/button-icon/button-icon.component';
import {DialogBase} from '../../../shared/components/dialogs/dialog-base/dialog-base';
import {TranslateSafePipe} from '../../../shared/pipes/translation.pipe';
import {formatTimeAgoTranslation} from '../../../shared/utils/date.util';
import {AccountRole} from '../../../types/dtos/account.dto';
import {AlarmDto, AlarmResponseDto} from '../../../types/dtos/alarm.dto';
import {AlarmSeverity, AlarmStatus} from '../../../types/enums/alarm.enum';
import {AlarmStatusUpdateDataInterface} from '../../../types/interfaces/alarm.interfaces';
import {TableTagSeverity} from '../../../types/interfaces/table.interface';
import {AlarmStatusUpdateDialog} from './alarm-status-update-dialog/alarm-status-update-dialog';
import {TranslateService} from '@ngx-translate/core';
import {LogService} from '../../../services/log.service';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../types/enums/logging.enum';

interface AlarmTimeFrame {
  title: string;
  time: string;
  iconColor: string;
}

@Component({
  selector: 'dialog-alarm-detail',
  imports: [
    Tag,
    ButtonIconComponent,
    Timeline,
    AlarmStatusUpdateDialog,
    DialogBase,
    RouterLink,
    TranslateSafePipe,
  ],
  templateUrl: './dialog-alarm-detail.html',
  styleUrl: './dialog-alarm-detail.css',
})
export class DialogAlarmDetail implements OnChanges, AfterViewInit {
  @Input()
  public alarm: AlarmDto | null = null;

  @Input()
  public loading: boolean = false;

  @Output()
  public statusAction: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public closeClick: EventEmitter<void> = new EventEmitter<void>();

  public readonly imagePath: string = window.location.protocol + '//' + window.location.hostname + environment.imageUrl;
  public isAlarmStatusUpdateOpen: boolean = false;
  public alarmStatusLoading: boolean = false;
  public timeFrame: AlarmTimeFrame[] = [];
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _alarmService: AlarmService,
    private readonly _accountService: AccountService,
    private readonly _authService: AuthService,
    private readonly _domSanitizer: DomSanitizer,
    private readonly _deviceService: DeviceService,
    protected readonly _translateService: TranslateService,
    private readonly _logService: LogService,
  ) {
  }

  ngAfterViewInit(): void {
    this._logService.sendLog({
      target: LoggingTargetEnum.ALARM,
      action: LoggingActionEnum.VIEW,
      description: 'Schaut sich alarm an',
      target_id: this.alarm?.id,
    });
    }

  public get statusLabel(): string {
    return this._alarmService.mapAlarmStatusLabel(this.alarm?.status ?? AlarmStatus.ACTIVE);
  }

  public get statusColor(): TableTagSeverity {
    return this._alarmService.mapAlarmStatusClass(this.alarm?.status ?? AlarmStatus.ACTIVE);
  }

  public get severityLabel(): string {
    return this._alarmService.mapAlarmSeverityLabel(this.alarm?.severity ?? AlarmSeverity.WARNING);
  }

  public get severityColor(): TableTagSeverity {
    return this._alarmService.mapAlarmSeverityClass(this.alarm?.severity ?? AlarmSeverity.WARNING);
  }

  public get alarmTypeLabel(): string {
    if (!this.alarm?.alarm_type) return '';
    return 'common.enum.alarm-type.' + this.alarm.alarm_type;
  }

  public get alarmStatusUpdateDialogTitle(): string {
    if (!this.alarm) return '';
    if (this.alarm.status === AlarmStatus.ACTIVE) {
      return this._translateService.instant('common.messages.alarm') + " " + (this._translateService.instant('common.actions.confirm')).toLowerCase();
    }

    if (this.alarm.status === AlarmStatus.ACKNOWLEDGED) {
      return this._translateService.instant('common.messages.alarm') + " " + (this._translateService.instant('common.messages.solve')).toLowerCase();
    }

    return this._translateService.instant('common.messages.alarm-status') + " " + (this._translateService.instant('common.action.change')).toLowerCase();
  }

  public getSerialNumber(deviceId: number): string | undefined {
    return this._deviceService.getDeviceNumber(deviceId);
  }

  // On new alarm status change
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['alarm']) {
      this.updateTimeFrame();
    }
  }

  public getUsernameFromId(id: number): string | null {
    return this._accountService.getUsernameOfUserId(id);
  }

  public canEdit(): boolean {
    return this._authService.canAccess(AccountRole.OPERATOR);
  }

  public hasStatusAction(): boolean {
    return this.alarm?.status === AlarmStatus.ACTIVE
      || this.alarm?.status === AlarmStatus.ACKNOWLEDGED;
  }

  public getStatusActionLabel(): string {
    if (this.alarm?.status === AlarmStatus.ACTIVE) {
      return this._translateService.instant('common.actions.confirm');
    }

    if (this.alarm?.status === AlarmStatus.ACKNOWLEDGED) {
      return this._translateService.instant('common.messages.solve');
    }

    return '';
  }

  public getStatusActionIcon(): string {
    if (this.alarm?.status === AlarmStatus.ACTIVE) {
      return 'check_circle';
    }

    if (this.alarm?.status === AlarmStatus.ACKNOWLEDGED) {
      return 'task_alt';
    }

    return '';
  }

  public onStatusActionClick(): void {
    if (!this.alarm || !this.hasStatusAction() || this.loading) {
      return;
    }

    this.isAlarmStatusUpdateOpen = true;
  }

  public bypassUrl(imageUrl: string): SafeUrl {
    return this._domSanitizer.bypassSecurityTrustUrl(imageUrl);
  }


  public readonly closeAlarmStatusUpdateDialog = (): void => {
    this.isAlarmStatusUpdateOpen = false;
  };

  public confirmAlarmStatusUpdate(updateData: AlarmStatusUpdateDataInterface): void {
    if (!this.alarm || this.alarmStatusLoading) {
      return;
    }

    if (updateData.status === AlarmStatus.ACTIVE) {
      this.acknowledgeAlarm(this.alarm, updateData);
      return;
    }

    if (updateData.status === AlarmStatus.ACKNOWLEDGED) {
      this.resolveAlarm(this.alarm, updateData);
    }
  }

  public formatTimeAgo(date: string): string {
    return formatTimeAgoTranslation(this._translateService, date);
  };

  private updateTimeFrame(): void {
    this.timeFrame = [];

    if (!this.alarm) return;

    this.timeFrame.push({
      title: this._translateService.instant('common.messages.alarm') + " " + (this._translateService.instant('common.fields.created')).toLowerCase(),
      time: formatTimeAgoTranslation(this._translateService, this.alarm.created_at),
      iconColor: 'var(--p-blue-600)',
    });

    if (this.alarm.acknowledged_at) {
      let acknowledgedTitle: string = this._translateService.instant('common.messages.alarm') + " " + (this._translateService.instant('common.enum.alarm-status.confirmed')).toLowerCase();
      if (this.alarm.acknowledged_by) {
        acknowledgedTitle += ` ${this._translateService.instant('common.messages.by')} ${this.getUsernameFromId(this.alarm.acknowledged_by)}`;
      }

      this.timeFrame.push({
        title: acknowledgedTitle,
        time: formatTimeAgoTranslation(this._translateService, this.alarm.acknowledged_at),
        iconColor: 'var(--p-orange-600)',
      });
    }

    if (this.alarm.resolved_at) {
      let resolvedTitle: string = this._translateService.instant('common.messages.alarm') + " " + (this._translateService.instant('common.enum.alarm-status.resolved')).toLowerCase();
      if (this.alarm.resolved_by) {
        resolvedTitle += ` ${this._translateService.instant('common.messages.by')} ${this.getUsernameFromId(this.alarm.resolved_by)}`;
      }

      this.timeFrame.push({
        title: resolvedTitle,
        time: formatTimeAgoTranslation(this._translateService, this.alarm.resolved_at),
        iconColor: 'var(--p-green-600)',
      });
    }
  }

  private acknowledgeAlarm(alarm: AlarmDto, updateData: AlarmStatusUpdateDataInterface): void {
    this.alarmStatusLoading = true;

    this._alarmService.acknowledged(alarm.id, updateData)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result: AlarmResponseDto) => {
          this.alarmStatusLoading = false;
          this.closeAlarmStatusUpdateDialog();
          this.alarm = result.data ?? this.alarm;
          this.updateTimeFrame();
        },
        error: (error: unknown) => {
          console.error('Error acknowledging alarm:', error);
          this.alarmStatusLoading = false;
        },
      });
  }

  private resolveAlarm(alarm: AlarmDto, updateData: AlarmStatusUpdateDataInterface): void {
    this.alarmStatusLoading = true;

    this._alarmService.resolved(alarm.id, updateData)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result: AlarmResponseDto) => {
          this.alarmStatusLoading = false;
          this.closeAlarmStatusUpdateDialog();
          this.alarm = result.data ?? this.alarm;
          this.updateTimeFrame();
        },
        error: (error: unknown) => {
          console.error('Error resolving alarm:', error);
          this.alarmStatusLoading = false;
        },
      });
  }

  protected readonly formatTimeAgoTranslation = formatTimeAgoTranslation;
}
